import { MyOptions, ChartOptions, DataSet } from "@/charts/graphData";
import { IDashboardData } from "@/types/Dashboard.model";
import { V2GetAllDataResult, LegacyDataFormat } from "@/types/GetAllData.model";
import { Enseigne } from "@/shared/Enseigne";
import moment from "moment/moment";
import jsonURL from "@/shared/urls";
import store from "@/store/store";

export class DashboardDataService {
  private cachedV2Data: V2GetAllDataResult[] = [];

  public async fetchDashboardData(
    myOptions: MyOptions,
    isTTC: boolean,
    client: string,
    getGlobal: boolean,
    enseignes: any[]
  ): Promise<IDashboardData> {
    const dashboardData: IDashboardData = this.initializeDashboardData();

    const config = {
      site: getGlobal ? myOptions.site : myOptions.site[0],
      periode: "J",
      date: moment(myOptions.date).format("YYYY-MM-DD"),
      profit: [],
      localisation: "",
      service: myOptions.service,
      client,
      heureCourante:
        moment(myOptions.date).format("YYYY-MM-DD") ===
          moment(Date.now()).format("YYYY-MM-DD")
          ? true
          : false,
    };

    const url = isTTC ? jsonURL.getAllData : jsonURL.getAllDataHT;
    const wsEndpoint = "V2/getAllDataByProfit";

    console.log(`DashboardDataService conditions => client: ${client}, global: ${getGlobal}`);

    // Toujours utiliser l'API V2 getAllDataByProfit (nouvelle version)
    try {
      console.log("DashboardDataService: Using V2 API getAllDataByProfit");
      const data: V2GetAllDataResult[] = await store.dispatch("home/getHomeDataV2", {
        config,
        WS: wsEndpoint
      });
      this.cachedV2Data = data;
      this.processV2Data(data, dashboardData, myOptions, enseignes, isTTC);
    } catch (error) {
      console.error("DashboardDataService: V2 API failed, falling back to Legacy:", error);
      // Fallback to Legacy API en cas d'erreur
      dashboardData.hasCommandesEnCoursData = false;
      const legacyData = await store.dispatch("home/getHomeData", {
        config,
        WS: url
      });
      this.processLegacyData(legacyData, dashboardData, myOptions);
    }

    await this.completeDataProcessing(dashboardData, myOptions, getGlobal, client, config);

    return dashboardData;
  }

  public reprocessDashboardData(
    myOptions: MyOptions,
    isTTC: boolean,
    client: string,
    getGlobal: boolean,
    enseignes: any[]
  ): IDashboardData {
    const dashboardData: IDashboardData = this.initializeDashboardData();

    console.log(`reprocessing with isTTC: ${isTTC}, service: ${myOptions.service}, cached data length: ${this.cachedV2Data ? this.cachedV2Data.length : 0}`);

    if (this.cachedV2Data && this.cachedV2Data.length > 0) {
      console.log(`Processing cached V2 data with new settings - TTC: ${isTTC}, Service: ${myOptions.service}`);
      this.processV2Data(this.cachedV2Data, dashboardData, myOptions, enseignes, isTTC);
      console.log(`Processed data - TOTAL: ${dashboardData.CAParService.TOTAL}, Services found: ${dashboardData.services.join(", ")}`);

      // IMPORTANT: Finaliser le traitement pour configurer les titres et options des graphiques
      console.log("Reprocessing - Finalizing data processing for chart options...");
      this.finalizeDataProcessing(dashboardData);
    } else {
      console.warn("No cached V2 data available for reprocessing");
    }

    return dashboardData;
  }

  private initializeDashboardData(): IDashboardData {
    return {
      CAParService: { TOTAL: 0 },
      CAParEnseignes: {},
      CAParOrderSource: {},
      TicketsEtCouverts: {
        "TOTAL": "0/0/0",
        "Hier": "0/0/0",
        "Semaine dernière": "0/0/0",
        "Mois dernier": "0/0/0",
        "Année dernière": "0/0/0",
        "CA EN COURS": "0/0/0",
      },
      CACouverts: 0,
      CATickets: 0,
      CATables: 0,
      CACouvertsParEnseignes: {},
      CATicketsParEnseignes: {},
      CATablesParEnseignes: {},
      CAEnCours: 0,
      CouvertsEnCours: 0,
      TablesEnCours: 0,
      hasCommandesEnCoursData: false,
      CAEnCoursParEnseignes: {},
      CouvertsEnCoursParEnseignes: {},
      TablesEnCoursParEnseignes: {},
      CAParVendeurs: {},
      CAVendeursSeries: [],
      activeVendeurs: [],
      activeVendeurs2: [],
      activeVendeurs3: [],
      remunerationParVendeurs: [],
      CouvertsMoyenParVendeurs: {},
      CouvertsVendeursSeries: [],
      tabCA: [],
      donutLabels: [],
      donutLabelsOrderSource: [],
      orderSourceSeries: [],
      colors: [
        "#26A69A",
        "#1976D2",
        "#EF5350",
        "#FFD54F",
        "#7E57C2",
        "#03C9A9",
        "#C0392B",
        "#2574A9",
        "#6C7A89",
        "#1EBC61",
        "#BD02B0",
        "#0057A0",
        "#1EBC61",
        "#870000",
      ],
      enseigneColor: {},
      orderSourceColor: {},
      services: [],
      nbFinished: 0,
      reRender: false,
      donutOptions: new ChartOptions(),
      donutOptions2: new ChartOptions(),
      donutOptions3: new ChartOptions(),
      donutOptions4: new ChartOptions(),
      dataSetAll: new DataSet(),
      barLabels: [],
    };
  }

  private processLegacyData(data: { [key: string]: any }, dashboardData: IDashboardData, myOptions: MyOptions) {
    const enseigneCounter = 1;

    for (let key of Object.keys(data).sort()) {
      const splittedData = data[key];
      const value = parseFloat(splittedData[3]);
      const nbCouverts = parseInt(splittedData[2]);
      const nbTickets = parseInt(splittedData[0]);
      const nbTables = parseInt(splittedData[1]);

      key = key
        .replace(/%20/g, " ")
        .replace(/%C3%B4/g, "ô")
        .replace(/%C3%A9/g, "é");

      const splittedKey = key.split("/");
      const profit = splittedKey[0];
      const tag = splittedKey[1];
      const vendeur = splittedKey[2];
      const service = splittedKey[3].trim();

      if (myOptions.service === "GLOBAL" || service === myOptions.service) {
        this.updateDashboardTotals(dashboardData, splittedData, value);
        this.processVendeurData(dashboardData, vendeur, value, nbCouverts);
        this.processTagData(dashboardData, tag, value, nbCouverts, nbTickets, nbTables, splittedData, enseigneCounter);
        this.processProfitData(dashboardData, profit, value, splittedData);
        this.updateCAMetrics(dashboardData, value, nbCouverts, nbTickets, nbTables);
      }

      if (!dashboardData.services.includes(service)) {
        console.log("DashboardDataService - Adding service:", service);
        dashboardData.services.push(service);
      }
    }

    this.finalizeLegacyDataProcessing(dashboardData);
  }


  private processV2Data(v2Data: V2GetAllDataResult[], dashboardData: IDashboardData, myOptions: MyOptions, enseignes: any[], isTTC: boolean) {
    this.processV2CommandesEnCours(v2Data, dashboardData, myOptions, enseignes, isTTC);

    // Debug: afficher les données V2 brutes
    console.log("V2 raw data for tickets/couverts/tables:", v2Data.map((item) => ({
      restaurantId: item.restaurantId,
      nbTickets: item.nbTickets,
      nbCouverts: item.nbCouverts,
      nbTables: item.nbTables,
      caEncaisse: isTTC ? item.caEncaisse.ttc : item.caEncaisse.ht
    })));

    const transformedData = this.transformV2DataToLegacyFormat(v2Data, enseignes, isTTC);

    console.log("Transformed V2 data to legacy format:", transformedData);

    this.processLegacyData(transformedData, dashboardData, myOptions);

    // Calculer "CA EN COURS" = TOTAL + COMMANDES EN COURS
    this.calculateCAEnCours(dashboardData);
  }

  private processV2CommandesEnCours(v2Data: V2GetAllDataResult[], dashboardData: IDashboardData, myOptions: MyOptions, enseignes: any[], isTTC: boolean) {
    dashboardData.CAEnCours = 0;
    dashboardData.CouvertsEnCours = 0;
    dashboardData.TablesEnCours = 0;
    dashboardData.CAEnCoursParEnseignes = {};
    dashboardData.CouvertsEnCoursParEnseignes = {};
    dashboardData.TablesEnCoursParEnseignes = {};
    dashboardData.TicketsEtCouverts["COMMANDES EN COURS"] = "0/0/0";

    v2Data.forEach((item) => {
      const caEnCoursValue = isTTC ? item.caEnCours.ttc : item.caEnCours.ht;

      if (caEnCoursValue > 0 || item.nbCouvertsEnCours > 0 || item.nbTablesEnCours > 0) {
        dashboardData.hasCommandesEnCoursData = true;

        const enseigneObj = enseignes.find((e: any) => e.restaurantId === item.restaurantId);
        const enseigne = enseigneObj ? enseigneObj.libelle : "";

        if (myOptions.service === "GLOBAL" || item.service === myOptions.service) {
          dashboardData.CAEnCours += caEnCoursValue;
          dashboardData.CouvertsEnCours += item.nbCouvertsEnCours;
          dashboardData.TablesEnCours += item.nbTablesEnCours;

          if (enseigne) {
            if (enseigne in dashboardData.CAEnCoursParEnseignes) {
              dashboardData.CAEnCoursParEnseignes[enseigne] += caEnCoursValue;
            } else {
              dashboardData.CAEnCoursParEnseignes[enseigne] = caEnCoursValue;
            }

            if (enseigne in dashboardData.CouvertsEnCoursParEnseignes) {
              dashboardData.CouvertsEnCoursParEnseignes[enseigne] += item.nbCouvertsEnCours;
            } else {
              dashboardData.CouvertsEnCoursParEnseignes[enseigne] = item.nbCouvertsEnCours;
            }

            if (enseigne in dashboardData.TablesEnCoursParEnseignes) {
              dashboardData.TablesEnCoursParEnseignes[enseigne] += item.nbTablesEnCours;
            } else {
              dashboardData.TablesEnCoursParEnseignes[enseigne] = item.nbTablesEnCours;
            }
          }

          if ("COMMANDES EN COURS" in dashboardData.TicketsEtCouverts && dashboardData.TicketsEtCouverts["COMMANDES EN COURS"]) {
            const existing = dashboardData.TicketsEtCouverts["COMMANDES EN COURS"].split("/");
            const newTickets = parseInt(existing[0]) + 0;
            const newTables = parseInt(existing[1]) + item.nbTablesEnCours;
            const newCouverts = parseInt(existing[2]) + item.nbCouvertsEnCours;
            dashboardData.TicketsEtCouverts["COMMANDES EN COURS"] = `${newTickets}/${newTables}/${newCouverts}`;
          } else {
            dashboardData.TicketsEtCouverts["COMMANDES EN COURS"] = `0/${item.nbTablesEnCours}/${item.nbCouvertsEnCours}`;
          }
        }
      }
    });
  }

  private calculateCAEnCours(dashboardData: IDashboardData) {
    // Additionner TOTAL + COMMANDES EN COURS pour créer CA EN COURS
    const total = dashboardData.TicketsEtCouverts["TOTAL"] ? dashboardData.TicketsEtCouverts["TOTAL"].split("/") : ["0", "0", "0"];
    const commandesEnCours = dashboardData.TicketsEtCouverts["COMMANDES EN COURS"] ? dashboardData.TicketsEtCouverts["COMMANDES EN COURS"].split("/") : ["0", "0", "0"];

    const ticketsTotal = parseInt(total[0]) + parseInt(commandesEnCours[0]);
    const tablesTotal = parseInt(total[1]) + parseInt(commandesEnCours[1]);
    const couvertsTotal = parseInt(total[2]) + parseInt(commandesEnCours[2]);

    dashboardData.TicketsEtCouverts["CA EN COURS"] = `${ticketsTotal}/${tablesTotal}/${couvertsTotal}`;
  }

  private transformV2DataToLegacyFormat(v2Data: V2GetAllDataResult[], enseignes: any[], isTTC: boolean): LegacyDataFormat {
    const result: LegacyDataFormat = {};
    v2Data.forEach((item) => {
      const centreProfit = item.profit;
      const webOrderSource = item.webOrderSource.length > 0 ? `&webordersource=${item.webOrderSource}` : "";
      const enseigneObj = enseignes.find((e: Enseigne) => e.restaurantId === item.restaurantId);
      const enseigne = item.enseigne || (enseigneObj ? enseigneObj.libelle : "");
      const tags = `destination=${centreProfit}&enseigne=${enseigne}&codeAdoria=v${item.restaurantId}${webOrderSource}`;
      const service = (item.service && item.service.length > 0) ? `${item.service}` : "GLOBAL";

      const key = `${centreProfit}/${tags}/${item.libVendeur}/${service}/${item.restaurantId}`;

      const caEncaisseValue = isTTC ? item.caEncaisse.ttc : item.caEncaisse.ht;

      if (key in result) {
        result[key][0] += item.nbTickets;
        result[key][1] += item.nbTables;
        result[key][2] += item.nbCouverts;
        result[key][3] += caEncaisseValue;
      } else {
        result[key] = [
          item.nbTickets,
          item.nbTables,
          item.nbCouverts,
          caEncaisseValue,
          item.restaurantId
        ];
      }
    });

    return result;
  }

  private updateDashboardTotals(dashboardData: IDashboardData, splittedData: any[], value: number) {
    dashboardData.TicketsEtCouverts["TOTAL"] = dashboardData.TicketsEtCouverts["TOTAL"]
      .split("/")
      .map((num, idx) =>
        (Number(num) + Number(splittedData[idx])).toString()
      )
      .join("/");
    dashboardData.CAParService["TOTAL"] += value;
  }

  private processVendeurData(dashboardData: IDashboardData, vendeur: string, value: number, nbCouverts: number) {
    if (vendeur in dashboardData.CAParVendeurs && vendeur[0] !== "@") {
      dashboardData.CAParVendeurs[vendeur] += value;
      dashboardData.CouvertsMoyenParVendeurs[vendeur] += nbCouverts;
    } else if (vendeur[0] !== "@") {
      dashboardData.CAParVendeurs[vendeur] = value;
      dashboardData.CouvertsMoyenParVendeurs[vendeur] = nbCouverts;
    }
  }

  private processTagData(dashboardData: IDashboardData, tag: string, value: number, nbCouverts: number, nbTickets: number, nbTables: number, splittedData: any[], enseigneCounter: number) {
    for (const word of tag.split("&")) {
      if (word.split("=")[0] === "enseigne") {
        const enseigne = word.split("=")[1];
        this.processEnseigneData(dashboardData, enseigne, value, nbCouverts, nbTickets, nbTables, splittedData);
      }
      if (word.split("=")[0] === "webordersource") {
        const enseigne = word.split("=")[1].replace(/%26/g, "&");
        this.processWebOrderSource(dashboardData, enseigne, value, splittedData);
      }
    }
  }

  private processEnseigneData(dashboardData: IDashboardData, enseigne: string, value: number, nbCouverts: number, nbTickets: number, nbTables: number, splittedData: any[]) {
    if (enseigne.toLowerCase() !== "bun meat bun") {
      if (!(enseigne in dashboardData.TicketsEtCouverts)) {
        dashboardData.TicketsEtCouverts[enseigne] = splittedData.slice(0, 3).join("/");
      } else {
        dashboardData.TicketsEtCouverts[enseigne] = dashboardData.TicketsEtCouverts[enseigne]
          .split("/")
          .map((num, idx) =>
            (Number(num) + Number(splittedData[idx])).toString()
          )
          .join("/");
      }
    }

    // Toujours traiter les enseignes, même si elles sont déjà dans CAParOrderSource
    if (enseigne in dashboardData.CAParEnseignes) {
      dashboardData.CAParEnseignes[enseigne] += value;
    } else {
      dashboardData.CAParEnseignes[enseigne] = value;
    }

    this.updateEnseigneMetrics(dashboardData, enseigne, value, nbCouverts, nbTickets, nbTables);
  }

  private processWebOrderSource(dashboardData: IDashboardData, enseigne: string, value: number, splittedData: any[]) {
    if (!(enseigne in dashboardData.TicketsEtCouverts)) {
      dashboardData.TicketsEtCouverts[enseigne] = splittedData.slice(0, 3).join("/");
    } else {
      dashboardData.TicketsEtCouverts[enseigne] = dashboardData.TicketsEtCouverts[enseigne]
        .split("/")
        .map((num, idx) =>
          (Number(num) + Number(splittedData[idx])).toString()
        )
        .join("/");
    }

    if (!(enseigne in dashboardData.CAParEnseignes)) {
      if (enseigne in dashboardData.CAParOrderSource) {
        dashboardData.CAParOrderSource[enseigne] += value;
        // Mettre à jour la série existante
        const existingIndex = dashboardData.donutLabelsOrderSource.indexOf(enseigne);
        if (existingIndex !== -1) {
          dashboardData.orderSourceSeries[existingIndex] += value;
        }
      } else {
        dashboardData.CAParOrderSource[enseigne] = value;
        dashboardData.orderSourceSeries.push(value);
        dashboardData.donutLabelsOrderSource.push(enseigne);
      }
    }
  }

  private updateEnseigneMetrics(dashboardData: IDashboardData, enseigne: string, value: number, nbCouverts: number, nbTickets: number, nbTables: number) {
    if (nbCouverts !== 0) {
      if (enseigne in dashboardData.CACouvertsParEnseignes) {
        dashboardData.CACouvertsParEnseignes[enseigne] += value;
      } else {
        dashboardData.CACouvertsParEnseignes[enseigne] = value;
      }
    }

    if (nbTickets !== 0) {
      if (enseigne in dashboardData.CATicketsParEnseignes) {
        dashboardData.CATicketsParEnseignes[enseigne] += value;
      } else {
        dashboardData.CATicketsParEnseignes[enseigne] = value;
      }
    }

    if (nbTables !== 0) {
      if (enseigne in dashboardData.CATablesParEnseignes) {
        dashboardData.CATablesParEnseignes[enseigne] += value;
      } else {
        dashboardData.CATablesParEnseignes[enseigne] = value;
      }
    }
  }

  private processProfitData(dashboardData: IDashboardData, profit: string, value: number, splittedData: any[]) {
    // Handle destination mapping
    switch (profit) {
      case "EAT_IN":
      case "SALLE":
        profit = "Salle";
        break;
      case "TAKE_OUT":
        profit = "VAE";
        break;
      case "DELIVERY":
        profit = "Livraison";
        break;
      default:
        break;
    }

    if (dashboardData.donutLabels.includes(profit)) {
      dashboardData.CAParService[profit] += value;
    } else {
      dashboardData.CAParService[profit] = value;
      dashboardData.donutLabels.push(profit);
    }

    if (profit in dashboardData.TicketsEtCouverts) {
      dashboardData.TicketsEtCouverts[profit] = dashboardData.TicketsEtCouverts[profit]
        .split("/")
        .map((num, idx) =>
          (Number(num) + Number(splittedData[idx])).toString()
        )
        .join("/");
    } else {
      dashboardData.TicketsEtCouverts[profit] = splittedData.slice(0, 3).join("/");
    }
  }

  private updateCAMetrics(dashboardData: IDashboardData, value: number, nbCouverts: number, nbTickets: number, nbTables: number) {
    if (nbCouverts !== 0) {
      dashboardData.CACouverts += value;
    }
    if (nbTickets !== 0) {
      dashboardData.CATickets += value;
    }
    if (nbTables !== 0) {
      dashboardData.CATables += value;
    }
  }

  private finalizeLegacyDataProcessing(dashboardData: IDashboardData) {
    // Build tabCA array
    for (const [key, value] of Object.entries(dashboardData.CAParService)) {
      if (key !== "TOTAL") {
        dashboardData.tabCA.push(value);
      }
    }

    // Sort data
    dashboardData.donutLabels.sort(
      (a, b) =>
        dashboardData.tabCA[dashboardData.donutLabels.indexOf(b)] -
        dashboardData.tabCA[dashboardData.donutLabels.indexOf(a)]
    );
    dashboardData.tabCA.sort((a, b) => b - a);

    // Set colors
    dashboardData.donutLabels.forEach((value, index) => {
      dashboardData.enseigneColor[value] = dashboardData.colors[index];
    });

    // Process vendeur data
    for (const [key, value] of Object.entries(dashboardData.CAParVendeurs)) {
      dashboardData.CAVendeursSeries.push(value);
      dashboardData.activeVendeurs.push(key);
      dashboardData.activeVendeurs3.push(key);
    }

    for (const value of Object.values(dashboardData.CouvertsMoyenParVendeurs)) {
      dashboardData.CouvertsVendeursSeries.push(value);
    }

    dashboardData.CAVendeursSeries.forEach((value, index) => {
      const nbCouverts = dashboardData.CouvertsVendeursSeries[index];
      dashboardData.CouvertsVendeursSeries[index] = nbCouverts > 0 ? value / nbCouverts : 0;
    });

    // Sort vendeur data
    dashboardData.activeVendeurs.sort(
      (a, b) =>
        dashboardData.CAVendeursSeries[dashboardData.activeVendeurs.indexOf(b)] -
        dashboardData.CAVendeursSeries[dashboardData.activeVendeurs.indexOf(a)]
    );
    dashboardData.CAVendeursSeries.sort((a, b) => b - a);

    dashboardData.activeVendeurs3.sort(
      (a, b) =>
        dashboardData.CouvertsVendeursSeries[dashboardData.activeVendeurs3.indexOf(b)] -
        dashboardData.CouvertsVendeursSeries[dashboardData.activeVendeurs3.indexOf(a)]
    );
    dashboardData.CouvertsVendeursSeries.sort((a, b) => b - a);

    // Sort order source data
    dashboardData.donutLabelsOrderSource.sort(
      (a, b) =>
        dashboardData.orderSourceSeries[dashboardData.donutLabelsOrderSource.indexOf(b)] -
        dashboardData.orderSourceSeries[dashboardData.donutLabelsOrderSource.indexOf(a)]
    );
    dashboardData.orderSourceSeries.sort((a, b) => b - a);

    dashboardData.donutLabelsOrderSource.forEach((value, index) => {
      dashboardData.orderSourceColor[value] = dashboardData.colors[index];
    });
  }

  private async completeDataProcessing(dashboardData: IDashboardData, myOptions: MyOptions, getGlobal: boolean, client: string, config: any) {
    // Handle BK specific data
    if (
      getGlobal &&
      client === "BK" &&
      "COMPTOIR" in dashboardData.TicketsEtCouverts
    ) {
      const data = await store.dispatch("home/getHomeData", {
        config,
        WS: jsonURL.updateNbTicketsRepasEmployes,
      });
      this.processBKSpecificData(data, dashboardData, myOptions);
    }

    // Handle remuneration data
    dashboardData.activeVendeurs2 = [];
    if (
      !getGlobal ||
      (client === "BUFFALO" && myOptions.site.length === 1)
    ) {
      const data = await store.dispatch("home/getHomeData", {
        config,
        WS: jsonURL.updateRemunerationParVendeurServicePerso,
      });
      this.processRemunerationData(data, dashboardData, myOptions);
    }

    this.finalizeDataProcessing(dashboardData);
  }

  private processBKSpecificData(data: any, dashboardData: IDashboardData, myOptions: MyOptions) {
    for (const key of Object.keys(data)) {
      const value = data[key] !== undefined ? parseFloat(data[key][0]) : 0;
      const tabBuff = [value, 0, 0];
      if (value > 0 && (myOptions.service === "GLOBAL" || key === myOptions.service)) {
        dashboardData.TicketsEtCouverts["COMPTOIR"] = dashboardData.TicketsEtCouverts["COMPTOIR"]
          .split("/")
          .map((num, idx) =>
            (Number(num) + Number(tabBuff[idx])).toString()
          )
          .join("/");
        dashboardData.TicketsEtCouverts["TOTAL"] = dashboardData.TicketsEtCouverts["TOTAL"]
          .split("/")
          .map((num, idx) =>
            (Number(num) + Number(tabBuff[idx])).toString()
          )
          .join("/");
      }
    }
  }

  private processRemunerationData(data: any, dashboardData: IDashboardData, myOptions: MyOptions) {
    dashboardData.remunerationParVendeurs = [];
    for (const key of Object.keys(data)) {
      const value = data[key] !== undefined ? parseFloat(data[key][0]) : 0;
      const splittedKey = key.split("/");
      const vendeur = splittedKey[0];
      const service = splittedKey[1];
      if (
        value > 0 &&
        (myOptions.service === "GLOBAL" || service === myOptions.service)
      ) {
        dashboardData.remunerationParVendeurs.push(value);
        dashboardData.activeVendeurs2.push(vendeur);
      }
    }
    dashboardData.activeVendeurs2.sort(
      (a, b) =>
        dashboardData.remunerationParVendeurs[dashboardData.activeVendeurs2.indexOf(b)] -
        dashboardData.remunerationParVendeurs[dashboardData.activeVendeurs2.indexOf(a)]
    );
    dashboardData.remunerationParVendeurs.sort((a, b) => b - a);
  }

  private finalizeDataProcessing(dashboardData: IDashboardData) {
    // Setup chart options
    dashboardData.donutOptions.setTitleTxt("CA par vendeur");
    dashboardData.donutOptions2.setTitleTxt("Rémunération par vendeur");
    dashboardData.donutOptions3.setTitleTxt("CA par centre de profit");
    dashboardData.donutOptions4.setTitleTxt("CA par source de commande");

    dashboardData.donutOptions.makeMeADoughnutWithoutLabels();
    dashboardData.donutOptions2.makeMeADoughnutWithoutLabels();
    dashboardData.donutOptions3.makeMeADoughnutWithoutLabels();
    dashboardData.donutOptions4.makeMeADoughnutWithoutLabels();

    // Round numbers
    dashboardData.tabCA = dashboardData.tabCA.map((x) => Math.round(x * 100) / 100);
    dashboardData.orderSourceSeries = dashboardData.orderSourceSeries.map(
      (x) => Math.round(x * 100) / 100
    );
    dashboardData.CAVendeursSeries = dashboardData.CAVendeursSeries.map(
      (x) => Math.round(x * 100) / 100
    );
    dashboardData.remunerationParVendeurs = dashboardData.remunerationParVendeurs.map(
      (x) => Math.round(x * 100) / 100
    );
    dashboardData.CouvertsVendeursSeries = dashboardData.CouvertsVendeursSeries.map(
      (x) => Math.round(x * 100) / 100
    );

    // Setup bar chart data
    dashboardData.dataSetAll.label = "ALL";
    dashboardData.dataSetAll.data = new Array();
    dashboardData.CouvertsVendeursSeries.forEach((value: number, index: number) => {
      if (value > 0 && value !== Infinity) {
        dashboardData.dataSetAll.data.push(value);
        dashboardData.barLabels.push(dashboardData.activeVendeurs3[index]);
      }
    });

    dashboardData.reRender = !dashboardData.reRender;

    console.log("DashboardDataService - Final services list:", dashboardData.services);
  }
}
